import { Flex, Grid, Text } from "@chakra-ui/react";
import { NumberInput, RemoveButton, Select } from "@components/ui-kit";
import { Trans } from "@lingui/macro";
import { NumberModel } from "@src/utils/models";
import { observer } from "mobx-react-lite";
import type { ExpenseVatItemForm } from "../../form";
import { useStore } from "../../store";

interface VatItemProps {
  id: string;
  item: ExpenseVatItemForm;
}

export const VatItem = observer<VatItemProps>(function VatItem({ id, item }) {
  const store = useStore();
  const { form } = store;
  const multipleVatItems = form.expense_vat_items.size > 1;

  const onChangeAmountInCurrency = (val: string) => {
    item.amount_in_currency.onChange(
      item.amount_in_currency.value.setValue(val),
    );
    store.setTotalAmountWithVatInCurrency();
  };

  const onChangeVatInCurrency = (val: string) => {
    const denominator = store.selectedCurrency!.denomination;
    const parsed = new NumberModel(0, denominator).setValue(val);
    item.vat_in_currency.onChange(parsed.denominatedValue);
    store.setTotalAmountWithVatInCurrency();
  };

  if (!store.selectedCurrency) {
    return null;
  }

  return (
    <Grid
      alignItems={{
        base: "end",
        md: "center",
      }}
      columnGap={{
        base: "2",
        md: "4",
      }}
      templateColumns={`1fr 1fr 1fr${multipleVatItems ? " 36px" : ""}`}
      mb={{
        base: "0",
        md: "2",
      }}
      py={{
        base: "4",
        md: "0",
      }}
      borderBottomWidth={{
        base: "thin",
        md: "0",
      }}
      borderBottomColor={{
        base: "gray.200",
        md: "unset",
      }}
    >
      <Flex
        align={{ base: "unset", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: "1", md: "4" }}
      >
        <Trans>Amount</Trans>
        <NumberInput
          currency
          suffix={store.selectedCurrency.code}
          decimalDigits={item.amount_in_currency.value.denominator}
          value={item.amount_in_currency.value.value.toString()}
          onChange={onChangeAmountInCurrency}
          error={item.amount_in_currency.error}
        />
      </Flex>
      <Select
        prefix={
          <Text fontSize="xs">
            <Trans>VAT </Trans>
          </Text>
        }
        value={item.vat_percent_informative.value.toString()}
        options={store.vatRatesOptions}
        onChange={(v) => {
          item.vat_percent_informative.onChange(Number(v));
          store.handleVatItemVatSelect();
        }}
      />
      <NumberInput
        currency
        suffix={store.selectedCurrency.code}
        decimalDigits={store.selectedCurrency.denomination}
        value={new NumberModel(
          item.vat_in_currency.value,
          store.selectedCurrency.denomination,
        ).value.toString()}
        onChange={onChangeVatInCurrency}
      />

      {multipleVatItems && (
        <Flex>
          <RemoveButton
            onClick={() => {
              form.removeExpenseVatItem(id);
              store.syncBudgetItemVatDefaults();
              store.setTotalAmountWithVatInCurrency();
            }}
          />
        </Flex>
      )}
    </Grid>
  );
});
